import { useCallback, useEffect, useRef, useState } from "react";
import { AuthorizationError, OperationError } from "@/shared/errors/auth-errors";
import { DAILY_CURSOR_EXPIRED_MESSAGE, DailyContractError, isDailyEnvelope } from "@/features/daily/api/daily.api";
import { useDailyPolling } from "@/features/daily/hooks/use-daily-polling";
import type { MetricQuality } from "@/features/daily/types/daily.types";

export type DailyResourceStatus = "idle" | "loading" | "ok" | "partial" | "stale" | "error" | "forbidden";

export type DailyErrorCode = "contract" | "forbidden" | "cursor_expired" | "request";

export type DailyError = {
    code: DailyErrorCode;
    message: string;
};

export type DailyResourceMeta = {
    // FR-009: when the page fetched is NOT when the evidence was produced. Both
    // travel separately so a screen can never pass one off as the other.
    fetchedAt: number | null;
    sourceAsOf: number | null;
    stale: boolean;
    quality: MetricQuality | null;
};

export type DailyResourceKey = {
    resource: string;
    scope: string;
    query: Readonly<Record<string, string>>;
};

export type DailyResourceOptions<T> = {
    identity: string;
    isData: (value: unknown) => value is T;
    pollMs?: number;
    ttlMs?: number;
    enabled?: boolean;
};

export type DailyResource<T> = {
    status: DailyResourceStatus;
    data: T | null;
    error: DailyError | null;
    meta: DailyResourceMeta;
    refresh: () => void;
};

type Snapshot<T> = {
    // CH-5: the resource key the data answers. Data is only ever shown for its own key.
    key: string | null;
    status: DailyResourceStatus;
    data: T | null;
    error: DailyError | null;
    meta: DailyResourceMeta;
};

const DEFAULT_TTL_MS = 30_000;
const MAX_CACHE_ENTRIES = 50;

const EMPTY_META: DailyResourceMeta = { fetchedAt: null, sourceAsOf: null, stale: false, quality: null };

type CacheEntry = {
    value: unknown;
    fetchedAt: number;
};

const entries = new Map<string, CacheEntry>();
let cachedIdentity: string | null = null;

export const dailyResourceCache = {
    /** A different principal (or a logout) may never read the previous one's data. */
    adoptIdentity: (identity: string): void => {
        if (cachedIdentity === identity) return;
        cachedIdentity = identity;
        entries.clear();
    },
    get: (key: string): CacheEntry | undefined => {
        const entry = entries.get(key);
        if (entry === undefined) return undefined;
        entries.delete(key);
        entries.set(key, entry);
        return entry;
    },
    set: (key: string, entry: CacheEntry): void => {
        entries.delete(key);
        entries.set(key, entry);
        while (entries.size > MAX_CACHE_ENTRIES) {
            const oldest = entries.keys().next();
            if (oldest.done === true) break;
            entries.delete(oldest.value);
        }
    },
    clear: (): void => {
        cachedIdentity = null;
        entries.clear();
    },
    size: (): number => entries.size,
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const readEnvelopeQuality = (value: unknown): { quality: MetricQuality; sourceAsOf: number | null } | null => {
    if (!isRecord(value) || !isDailyEnvelope(value.envelope)) return null;
    return { quality: value.envelope.quality, sourceAsOf: value.envelope.sourceAsOf };
};

// L44: the map is TOTAL — every quality the closed vocabulary can carry, plus the
// `null` of a payload that has no envelope, resolves to a deterministic status.
const STATUS_BY_QUALITY: Record<MetricQuality, DailyResourceStatus> = {
    ok: "ok",
    partial: "partial",
    insufficient: "partial",
    unsupported: "partial",
    stale: "stale",
    error: "error",
};

export const toDailyError = (error: unknown): DailyError => {
    if (error instanceof DailyContractError) return { code: "contract", message: error.message };
    if (error instanceof AuthorizationError) return { code: "forbidden", message: error.message };
    if (error instanceof OperationError && error.message === DAILY_CURSOR_EXPIRED_MESSAGE) {
        return { code: "cursor_expired", message: error.message };
    }
    if (error instanceof Error) return { code: "request", message: error.message };
    return { code: "request", message: "The Daily source could not be reached" };
};

const resolvedSnapshot = <T,>(key: string, data: T, fetchedAt: number): Snapshot<T> => {
    const envelope = readEnvelopeQuality(data);
    const quality = envelope === null ? null : envelope.quality;
    const status = quality === null ? "ok" : STATUS_BY_QUALITY[quality];
    return {
        key,
        status,
        data,
        error: null,
        meta: {
            fetchedAt,
            sourceAsOf: envelope === null ? null : envelope.sourceAsOf,
            stale: status === "stale",
            quality,
        },
    };
};

const normalizeQuery = (query: Readonly<Record<string, string>>): string =>
    Object.keys(query)
        .sort()
        .filter((name) => query[name] !== "")
        .map((name) => `${name}=${query[name]}`)
        .join("&");

export const useDailyResource = <T,>(
    key: DailyResourceKey,
    fetcher: (signal: AbortSignal) => Promise<T>,
    options: DailyResourceOptions<T>,
): DailyResource<T> => {
    const { identity, isData, pollMs, ttlMs = DEFAULT_TTL_MS, enabled = true } = options;
    const cacheKey = `${identity}|${key.scope}|${key.resource}|${normalizeQuery(key.query)}`;

    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;
    const isDataRef = useRef(isData);
    isDataRef.current = isData;

    const requestSeqRef = useRef(0);
    const inFlightRef = useRef<string | null>(null);
    const controllerRef = useRef<AbortController | null>(null);

    const [snapshot, setSnapshot] = useState<Snapshot<T>>({
        key: null,
        status: "idle",
        data: null,
        error: null,
        meta: EMPTY_META,
    });

    const load = useCallback(async (targetKey: string, force: boolean): Promise<void> => {
        // Dedupe: a manual refresh landing on top of an automatic one must not open
        // a second request for the same key.
        if (inFlightRef.current === targetKey) return;

        const cached = dailyResourceCache.get(targetKey);
        if (!force && cached !== undefined && Date.now() - cached.fetchedAt <= ttlMs && isDataRef.current(cached.value)) {
            setSnapshot(resolvedSnapshot(targetKey, cached.value, cached.fetchedAt));
            return;
        }

        const seq = requestSeqRef.current + 1;
        requestSeqRef.current = seq;
        const controller = new AbortController();
        controllerRef.current = controller;
        inFlightRef.current = targetKey;
        // CH-5: stale data is kept only for the SAME key. Another key's answer is a
        // different resource, never "what was on screen" for this one.
        setSnapshot((previous) =>
            previous.key === targetKey
                ? { ...previous, status: "loading" }
                : { key: targetKey, status: "loading", data: null, error: null, meta: EMPTY_META },
        );

        try {
            const data = await fetcherRef.current(controller.signal);
            // FR-009/SC-009: a response from a superseded request is discarded. Without
            // this guard a slow first query overwrites the fresher second one.
            if (seq !== requestSeqRef.current) return;
            const fetchedAt = Date.now();
            dailyResourceCache.set(targetKey, { value: data, fetchedAt });
            setSnapshot(resolvedSnapshot(targetKey, data, fetchedAt));
        } catch (error) {
            if (seq !== requestSeqRef.current || controller.signal.aborted) return;
            const dailyError = toDailyError(error);
            // FR-024: a failure keeps whatever was already on screen, together with its
            // age, instead of blanking the section or showing a zero.
            // `previous` is this key's own loading snapshot (CH-5), so what it carries is
            // only ever this key's earlier answer.
            setSnapshot((previous) => ({
                key: previous.key,
                status: dailyError.code === "forbidden" ? "forbidden" : "error",
                data: previous.data,
                error: dailyError,
                meta: previous.meta,
            }));
        } finally {
            if (inFlightRef.current === targetKey) inFlightRef.current = null;
        }
    }, [ttlMs]);

    useEffect(() => {
        if (!enabled) return undefined;
        dailyResourceCache.adoptIdentity(identity);
        void load(cacheKey, false);
        return () => {
            controllerRef.current?.abort();
            inFlightRef.current = null;
        };
    }, [cacheKey, enabled, identity, load]);

    const refresh = useCallback((): void => { void load(cacheKey, true); }, [cacheKey, load]);

    useDailyPolling({
        intervalMs: pollMs ?? 0,
        enabled: enabled && pollMs !== undefined && pollMs > 0,
        onTick: () => load(cacheKey, true),
    });

    // Between a key change and the effect that loads it, the snapshot still answers the
    // previous key: that render must not present it as this key's data.
    if (snapshot.key !== cacheKey) {
        return { status: enabled ? "loading" : "idle", data: null, error: null, meta: EMPTY_META, refresh };
    }
    return { status: snapshot.status, data: snapshot.data, error: snapshot.error, meta: snapshot.meta, refresh };
};
